/**
 * Owners list — OWNER · ACCESS · APARTMENTS · PROPERTY · STATUS.
 *
 * Rows are merged owner records (see `@/lib/owner-directory`), so `row.lessor`
 * and `row.account` are each independently nullable and every cell has to cope
 * with its half being absent. Columns fed by the billing record show "—" rather
 * than 0 when there is no billing record — an unknown is not a verified zero.
 *
 * Rows open the owner detail modal; edit/delete stay available per row
 * (revealed on row hover to keep the table calm).
 */
import { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronRight,
  ChevronDown,
  Building,
  Building2,
  AlertCircle,
  HelpCircle,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { HelpMark, HelpPoint } from "@/components/admin/v2/primitives/HelpNote";
import { useLanguage } from "@/contexts/LanguageContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { initialsFromName } from "@/lib/v2-format";
import { OwnerActionsMenu } from "@/components/admin/v2/OwnerActionsMenu";
import type { AdminProfileDirectoryRow } from "@/lib/fetch-admin-profiles-directory";
// Shared with the account-only owner profile — one rule for "which buildings is
// this account attached to", read by both surfaces.
import { accountPropertyList } from "@/lib/owner-account-link";
import {
  accountPortalStatus,
  isPlatformOwner,
  portalStatusLabel,
  ownerLinkStateFixLabel,
  type PortalStatus,
} from "@/lib/owner-directory";
import type { OwnerDirectoryListRow } from "@/hooks/v2/useOwnersDirectory";
import type {
  OwnerListRow,
  OwnerListProperty,
  OnboardingStage,
} from "@/hooks/v2/useOwnerDetail";

type Translate = (key: string, fallback: string) => string;

const AVATAR_TINTS = [
  "bg-teal-600",
  "bg-amber-500",
  "bg-emerald-600",
  "bg-orange-500",
  "bg-slate-600",
  "bg-rose-500",
];
function tintFor(id: string) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % 997;
  return AVATAR_TINTS[h % AVATAR_TINTS.length];
}

/** Shared pill shell so invoice + onboarding badges read as one family. */
function Pill({
  tone,
  children,
}: {
  tone: "emerald" | "amber" | "rose" | "sky" | "slate";
  children: React.ReactNode;
}) {
  const map = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
    rose: "bg-rose-50 text-rose-700 border-rose-100",
    sky: "bg-sky-50 text-sky-700 border-sky-100",
    slate: "bg-muted text-muted-foreground border-transparent",
  } as const;
  const dot = {
    emerald: "bg-emerald-500",
    amber: "bg-amber-500",
    rose: "bg-rose-500",
    sky: "bg-sky-500",
    slate: "bg-muted-foreground/40",
  } as const;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium",
        map[tone]
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", dot[tone])} />
      {children}
    </span>
  );
}

function Dash() {
  return <span className="text-sm text-muted-foreground/50">—</span>;
}

const STAGE_META_KEYS: Record<
  OnboardingStage,
  { tone: "emerald" | "sky" | "slate"; key: string; fallback: string }
> = {
  onboarded: {
    tone: "emerald",
    key: "admin.owners.stage.onboarded",
    fallback: "Onboarded",
  },
  invite_sent: {
    tone: "sky",
    key: "admin.owners.stage.inviteSent",
    fallback: "Invite sent",
  },
  pending: {
    tone: "slate",
    key: "admin.owners.stage.pending",
    fallback: "Onboarding pending",
  },
};

/** Aggregate onboarding stage pill for the owner Status column. */
function StagePill({
  stage,
  t,
}: {
  stage: OnboardingStage | null;
  t: Translate;
}) {
  if (!stage) return <Dash />;
  const m = STAGE_META_KEYS[stage];
  return <Pill tone={m.tone}>{t(m.key, m.fallback)}</Pill>;
}

/** "2 onboarded · 1 invite sent · 3 pending" summary across an owner's properties. */
function stageSummary(o: OwnerListRow, t: Translate): string {
  if (o.propertyList.length === 0)
    return t("admin.owners.stage.noProperties", "No properties");
  const counts: Record<OnboardingStage, number> = {
    onboarded: 0,
    invite_sent: 0,
    pending: 0,
  };
  for (const p of o.propertyList) counts[p.onboardingStage]++;
  const parts: string[] = [];
  if (counts.onboarded)
    parts.push(
      `${counts.onboarded} ${t("admin.owners.stage.onboardedLower", "onboarded")}`
    );
  if (counts.invite_sent)
    parts.push(
      `${counts.invite_sent} ${t("admin.owners.stage.inviteSentLower", "invite sent")}`
    );
  if (counts.pending)
    parts.push(
      `${counts.pending} ${t("admin.owners.stage.pendingLower", "pending")}`
    );
  return parts.join(" · ");
}

/**
 * Purple property tags shown in the owner's Property column — the same
 * treatment as the owner settings panel, and deliberately PROPERTY-level: an
 * owner's apartments roll up to the buildings they sit in rather than being
 * listed one by one. A single property is one badge; several collapse behind
 * a count.
 */
function PropertyTags({
  properties,
  open,
  onToggle,
  t,
}: {
  properties: OwnerListProperty[];
  open: boolean;
  onToggle: () => void;
  t: Translate;
}) {
  if (properties.length === 0) return null;

  const PURPLE =
    "border-purple-200/70 bg-purple-50/70 text-purple-900 dark:border-purple-800/50 dark:bg-purple-950/40 dark:text-purple-100";

  if (properties.length === 1) {
    const p = properties[0];
    return (
      <span
        className={cn(
          "inline-flex max-w-full items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-normal",
          PURPLE
        )}
      >
        <Building2 className="h-3 w-3 shrink-0 opacity-60" />
        <span className="truncate font-medium">{p.title ?? "—"}</span>
        {p.city && (
          <span className="whitespace-nowrap opacity-60">· {p.city}</span>
        )}
      </span>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full border py-0.5 pl-2 pr-2 text-xs font-medium transition-colors hover:bg-purple-100/70 dark:hover:bg-purple-900/40",
          PURPLE
        )}
      >
        <Building2 className="h-3.5 w-3.5 shrink-0 opacity-70" />
        {properties.length} {t("admin.owners.properties", "properties")}
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 opacity-70 transition-transform",
            open && "rotate-180"
          )}
        />
      </button>
      {open && (
        <div className="mt-1.5 flex flex-col gap-1 border-l-2 border-purple-200/70 pl-2.5 dark:border-purple-800/50">
          {properties.map((p) => (
            <div key={p.id} className="flex items-center gap-1.5 text-sm">
              <Building2 className="h-3.5 w-3.5 shrink-0 opacity-60" />
              <span className="font-medium">{p.title ?? "—"}</span>
              {p.city && (
                <span className="text-muted-foreground">· {p.city}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Tone map for the portal-status pill, mirroring the shared status rule. */
const PORTAL_TONE: Record<
  PortalStatus["tone"],
  "emerald" | "sky" | "amber" | "slate"
> = {
  emerald: "emerald",
  sky: "sky",
  amber: "amber",
  slate: "slate",
};

/**
 * Explains the Access column's shortcuts — create login, add billing entity,
 * login link, impersonation.
 * Shared by the desktop header and the mobile card, which has no header row
 * to carry it — the two must not drift into separate copies of this copy.
 */
function AccessHelpTrigger({ t }: { t: Translate }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          aria-label={t(
            "admin.owners.access.helpAria",
            "What do these labels and actions mean?"
          )}
          className="text-muted-foreground/60 hover:text-muted-foreground"
        >
          <HelpCircle className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[calc(100vw-2rem)] max-w-md space-y-3 text-sm leading-relaxed text-muted-foreground"
      >
        <p>
          {t(
            "admin.owners.access.help.intro1",
            "An owner can exist in two ways. There is the"
          )}{" "}
          <span className="font-medium text-foreground">
            {t("admin.owners.access.help.billingRecord", "billing record")}
          </span>{" "}
          {t(
            "admin.owners.access.help.intro2",
            "— the name we put on the management-fee invoice, with apartments attached. And there is the"
          )}{" "}
          <span className="font-medium text-foreground">
            {t("admin.owners.access.help.portalLogin", "portal login")}
          </span>{" "}
          {t(
            "admin.owners.access.help.intro3",
            "— an email and password that opens their own dashboard. The two are stored separately, so an owner can have one without the other."
          )}
        </p>
        <HelpPoint
          term={t(
            "admin.ownerDirectory.linkState.billing_only.fixLabel",
            "Create login"
          )}
        >
          {t(
            "admin.owners.access.help.billingOnly.body1",
            "We can invoice them, but"
          )}{" "}
          <HelpMark>
            {t(
              "admin.owners.access.help.billingOnly.mark",
              "they have no way to sign in"
            )}
          </HelpMark>{" "}
          {t(
            "admin.owners.access.help.billingOnly.body2",
            'and see anything. "Create login" makes them an account.'
          )}
        </HelpPoint>
        <HelpPoint
          term={t(
            "admin.ownerDirectory.linkState.account_only.fixLabel",
            "Add billing entity"
          )}
        >
          {t(
            "admin.owners.access.help.accountOnly.body1",
            "They can sign in, but there is no record to bill them against, so"
          )}{" "}
          <HelpMark>
            {t(
              "admin.owners.access.help.accountOnly.mark",
              "they never appear on an invoice run"
            )}
          </HelpMark>
          .{" "}
          {t(
            "admin.owners.access.help.accountOnly.body2",
            '"Add billing entity" creates one.'
          )}
        </HelpPoint>
        <HelpPoint
          term={t(
            "admin.owners.access.help.sendLoginLink.term",
            "Send login link"
          )}
        >
          {t(
            "admin.owners.access.help.sendLoginLink.body1",
            "Emails the owner a link that signs them in and lets them set a password."
          )}{" "}
          <HelpMark>
            {t(
              "admin.owners.access.help.sendLoginLink.mark1",
              "It goes to the owner, not to you."
            )}
          </HelpMark>{" "}
          <HelpMark tone="good">
            {t(
              "admin.owners.access.help.sendLoginLink.mark2",
              "Nothing else about their account changes"
            )}
          </HelpMark>
          ,{" "}
          {t(
            "admin.owners.access.help.sendLoginLink.body2",
            "and you can send it again if they lose it."
          )}
        </HelpPoint>
        <HelpPoint
          term={t("admin.owners.access.help.actAsOwner.term", "Act as owner")}
        >
          {t(
            "admin.owners.access.help.actAsOwner.body1",
            "Opens the portal as that owner sees it, so you can check what they are looking at when they ask."
          )}{" "}
          <HelpMark tone="good">
            {t(
              "admin.owners.access.help.actAsOwner.mark1",
              "No email is sent and they are not notified."
            )}
          </HelpMark>{" "}
          {t(
            "admin.owners.access.help.actAsOwner.body2",
            'Leave it with "Stop impersonating" to return to your own account.'
          )}{" "}
          <HelpMark>
            {t(
              "admin.owners.access.help.actAsOwner.mark2",
              "Admins and Super Admins only."
            )}
          </HelpMark>
        </HelpPoint>
      </PopoverContent>
    </Popover>
  );
}

/**
 * The Access cell: what the owner's login is doing, plus a shortcut to fill in
 * whichever half of their record is missing.
 *
 * It used to lead with a "Account + billing / Billing only" label. That said
 * which record happened to exist rather than where the owner stands, and it
 * sat amber on rows with nothing to fix; the list's Login pending / Onboarding
 * pending chips carry that now (COL-486). The platform lessor gets no
 * "Create login" — it never has an owner login, by design.
 */
function AccessCell({
  row,
  onFixLink,
  showHelp = false,
}: {
  row: OwnerDirectoryListRow;
  onFixLink?: (row: OwnerDirectoryListRow) => void;
  /** The mobile card has no header row to carry the Access help popover, so
      it asks AccessCell to render its own trigger instead. */
  showHelp?: boolean;
}) {
  const { t } = useLanguage();
  const status = accountPortalStatus(row.account);
  const fixLabel = isPlatformOwner(row)
    ? null
    : ownerLinkStateFixLabel(t, row.linkState);

  return (
    <div className="flex flex-col items-start gap-1">
      <span className="inline-flex items-center gap-1.5">
        <Pill tone={PORTAL_TONE[status.tone]}>
          {portalStatusLabel(t, status)}
        </Pill>
        {showHelp && <AccessHelpTrigger t={t} />}
      </span>
      {fixLabel && onFixLink && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onFixLink(row);
          }}
          className="text-[11px] font-semibold text-primary underline-offset-2 hover:underline"
        >
          {fixLabel}
        </button>
      )}
    </div>
  );
}

/** Avatar + name + email. The row and the small-screen card show the same
    identity block, so it lives in one place. */
function OwnerIdentity({
  row,
  t,
}: {
  row: OwnerDirectoryListRow;
  t: Translate;
}) {
  return (
    <div className="flex items-center gap-3">
      <span
        className={cn(
          "flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white shadow-sm ring-2 ring-background",
          tintFor(row.key)
        )}
      >
        {initialsFromName(row.displayName)}
      </span>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="truncate font-semibold leading-tight text-foreground">
            {row.displayName}
          </span>
          {/* The sales-demo account (COL-447). Shown because the flag is
              otherwise invisible: it is the one account that can switch into
              the tenant dashboard, and an admin needs to see which one that
              is without opening the menu. */}
          {row.account?.is_demo_account ? (
            <span className="shrink-0 rounded-full bg-indigo-100 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300">
              {t("admin.owners.demoPill", "Demo")}
            </span>
          ) : null}
        </div>
        {/* The email is the only thing telling two similarly named owners
            apart ("Test" vs "Tester"), and it is what the roles directory
            identified people by — so now that both live in this one table, it
            belongs on the row. */}
        {row.email && (
          <div className="truncate text-xs text-muted-foreground">
            {row.email}
          </div>
        )}
      </div>
    </div>
  );
}

/** Apartment count chip; expands the per-apartment list. Without a billing
    entity an apartment can't be attributed to this owner at all, so the count
    is unknown, not zero. */
function ApartmentsChip({
  owner,
  isOpen,
  onToggle,
  t,
}: {
  owner: OwnerListRow | null;
  isOpen: boolean;
  onToggle: () => void;
  t: Translate;
}) {
  if (!owner) return <Dash />;
  if (owner.apartmentsCount === 0)
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold tabular-nums text-muted-foreground/60">
        <Building className="h-3 w-3 opacity-60" />0
      </span>
    );
  const unit =
    owner.apartmentsCount === 1
      ? t("admin.owners.apartmentSingular", "apartment")
      : t("admin.owners.apartmentPlural", "apartments");
  const action = isOpen
    ? t("admin.owners.collapse", "collapse")
    : t("admin.owners.expand", "expand");
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold tabular-nums transition-colors",
        isOpen
          ? "bg-primary/10 text-primary ring-1 ring-primary/30"
          : "bg-muted text-foreground hover:bg-muted/70"
      )}
      title={`${owner.apartmentsCount} ${unit} — ${action}`}
      aria-label={`${owner.apartmentsCount} ${t(
        "admin.owners.apartmentPlural",
        "apartments"
      )} — ${action}`}
      aria-expanded={isOpen}
    >
      <Building className="h-3 w-3 opacity-60" />
      {owner.apartmentsCount}
      <ChevronDown
        className={cn("h-3 w-3 transition-transform", isOpen && "rotate-180")}
      />
    </button>
  );
}

/** The buildings this owner's apartments sit in, property-level, matching the
    purple tags in owner settings. An owner with a billing record but no
    apartment can't be billed or reported on, so say so in red instead. */
function PropertyCell({
  owner,
  properties,
  open,
  onToggle,
  t,
}: {
  owner: OwnerListRow | null;
  properties: OwnerListProperty[];
  open: boolean;
  onToggle: () => void;
  t: Translate;
}) {
  if (owner && owner.apartmentsCount === 0)
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-rose-600">
        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
        {t("admin.owners.noApartmentLinked", "No apartment linked")}
      </span>
    );
  return (
    <PropertyTags
      properties={properties}
      open={open}
      onToggle={onToggle}
      t={t}
    />
  );
}

/** Aggregate onboarding stage (lowest across properties). */
function StatusCell({
  owner,
  t,
}: {
  owner: OwnerListRow | null;
  t: Translate;
}) {
  if (!owner) return <Dash />;
  return (
    <span title={stageSummary(owner, t)} className="inline-flex cursor-default">
      <StagePill stage={owner.onboardingStage} t={t} />
    </span>
  );
}

/** Each apartment, the property it sits in, and that property's stage. */
function ApartmentBreakdown({
  owner,
  t,
  className,
}: {
  owner: OwnerListRow;
  t: Translate;
  className?: string;
}) {
  return (
    <div className={cn("divide-y divide-border/50", className)}>
      {owner.apartmentList.map((a) => (
        <div key={a.id} className="flex items-center gap-3 py-2.5">
          <Building className="h-4 w-4 shrink-0 text-muted-foreground" />
          {/* Straight to the apartment, not its building — the row names an
              apartment, and OwnerApartmentLink (Payments, owner modal) already
              routes there. The same unit must not land somewhere else depending
              on which surface you clicked it from. */}
          <Link
            to={`/admin/properties/${a.propertyId}/apartments/${a.id}`}
            onClick={(e) => e.stopPropagation()}
            className="min-w-0 flex-1 truncate text-sm font-medium text-primary hover:underline"
            title={`${t("admin.owners.openApartment", "Open apartment")} ${a.label} — ${a.propertyTitle ?? t("admin.owners.property", "property")}`}
          >
            {a.label}
          </Link>
          <span className="hidden min-w-0 max-w-[45%] truncate text-xs text-muted-foreground sm:inline">
            {[a.propertyTitle, a.propertyCity].filter(Boolean).join(" · ") ||
              "—"}
          </span>
          <StagePill stage={a.onboardingStage} t={t} />
        </div>
      ))}
    </div>
  );
}

/** Owner + account menu plus the "open profile" chevron. */
function RowActions({
  row,
  owner,
  isSuperAdmin,
  onRowClick,
  onEditOwner,
  onEditAccount,
  onDelete,
  onSendLoginLink,
  onFixLink,
}: {
  row: OwnerDirectoryListRow;
  owner: OwnerListRow | null;
  isSuperAdmin: boolean;
  onRowClick: (row: OwnerDirectoryListRow) => void;
  onEditOwner: (row: OwnerDirectoryListRow) => void;
  onEditAccount: (row: OwnerDirectoryListRow) => void;
  onDelete: (row: OwnerDirectoryListRow) => void;
  onSendLoginLink?: (row: OwnerDirectoryListRow) => void;
  onFixLink?: (row: OwnerDirectoryListRow) => void;
}) {
  return (
    <div className="flex items-center justify-end gap-1">
      <OwnerActionsMenu
        owner={{
          name: row.displayName,
          type: owner?.type ?? null,
          email: row.email,
          id: owner?.id ?? null,
        }}
        account={row.account}
        hasEntity={!!owner}
        isSuperAdmin={isSuperAdmin}
        onOpenProfile={() => onRowClick(row)}
        onEditOwner={() => onEditOwner(row)}
        onEditAccount={row.account ? () => onEditAccount(row) : undefined}
        onAddBillingEntity={onFixLink ? () => onFixLink(row) : undefined}
        onSendLoginLink={
          onSendLoginLink ? () => onSendLoginLink(row) : undefined
        }
        onDelete={() => onDelete(row)}
      />
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/40 transition-transform group-hover:translate-x-0.5 group-hover:text-muted-foreground" />
    </div>
  );
}

const HEAD =
  "h-12 whitespace-nowrap text-[11px] font-semibold uppercase tracking-wider text-muted-foreground";

export function OwnersTable({
  rows,
  onRowClick,
  onEditOwner,
  onEditAccount,
  onDelete,
  onSendLoginLink,
  onFixLink,
  propertiesById,
  isSuperAdmin = false,
}: {
  rows: OwnerDirectoryListRow[];
  onRowClick: (row: OwnerDirectoryListRow) => void;
  /** Edit the OWNER record (lessor). Menu item is hidden when there isn't one. */
  onEditOwner: (row: OwnerDirectoryListRow) => void;
  /** Edit the PORTAL ACCOUNT. Menu item is hidden when there isn't one. */
  onEditAccount: (row: OwnerDirectoryListRow) => void;
  onDelete: (row: OwnerDirectoryListRow) => void;
  onSendLoginLink?: (row: OwnerDirectoryListRow) => void;
  /** Create the missing half — a login, or a billing entity. */
  onFixLink?: (row: OwnerDirectoryListRow) => void;
  propertiesById?: Map<number, { title: string | null; city: string | null }>;
  isSuperAdmin?: boolean;
}) {
  const { t } = useLanguage();
  // One layout renders at a time rather than three hidden copies of every row:
  // 42 owners in a table plus the same 42 as cards is DOM nobody reads.
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  // The purple property tags under the name expand independently of the
  // apartments chip, so an owner can show their buildings without unfolding
  // the per-apartment list.
  const [propsOpen, setPropsOpen] = useState<Set<string>>(new Set());
  const togglePropsTag = (id: string) =>
    setPropsOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Phones get one card per owner. The table needed ~700px of horizontal
  // scroll on a phone, which hid the Access and Status cells behind a swipe —
  // the two things this list exists to show.
  if (isMobile) {
    return (
      <ul className="divide-y divide-border/60 overflow-hidden rounded-2xl border bg-card shadow-sm">
        {rows.map((row) => {
          const o = row.lessor;
          const key = row.key;
          const isOpen = expanded.has(key);
          const properties =
            o?.propertyList ?? accountPropertyList(row.account, propertiesById);
          return (
            <li
              key={key}
              role="button"
              tabIndex={0}
              onClick={() => onRowClick(row)}
              onKeyDown={(e) => {
                // Enter/Space on a nested control (the actions menu, the
                // apartments chip, an apartment link) must do that control's
                // job only — keydown bubbles even where onClick stops.
                if (e.target !== e.currentTarget) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onRowClick(row);
                }
              }}
              className="group flex cursor-pointer flex-col gap-3 p-4 transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <OwnerIdentity row={row} t={t} />
                </div>
                <RowActions
                  row={row}
                  owner={o}
                  isSuperAdmin={isSuperAdmin}
                  onRowClick={onRowClick}
                  onEditOwner={onEditOwner}
                  onEditAccount={onEditAccount}
                  onDelete={onDelete}
                  onSendLoginLink={onSendLoginLink}
                  onFixLink={onFixLink}
                />
              </div>

              <div className="flex flex-wrap items-center gap-2 pl-[3.25rem]">
                <PropertyCell
                  owner={o}
                  properties={properties}
                  open={propsOpen.has(key)}
                  onToggle={() => togglePropsTag(key)}
                  t={t}
                />
                <ApartmentsChip
                  owner={o}
                  isOpen={isOpen}
                  onToggle={() => toggle(key)}
                  t={t}
                />
              </div>

              <div className="flex flex-wrap items-start justify-between gap-2 pl-[3.25rem]">
                <AccessCell row={row} onFixLink={onFixLink} showHelp />
                <StatusCell owner={o} t={t} />
              </div>

              {isOpen && o && (
                <ApartmentBreakdown
                  owner={o}
                  t={t}
                  className="rounded-lg bg-muted/30 px-3"
                />
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  // Columns are percentages of the shell, so the width the widened layout
  // hands back goes into Owner and Property — the two cells that truncate —
  // instead of a right-hand gutter.
  return (
    <div className="overflow-x-auto rounded-2xl border bg-card shadow-sm">
      <Table className="min-w-[900px] table-fixed">
        <TableHeader className="[&_tr]:border-b [&_tr]:bg-muted/40 [&_tr]:hover:bg-muted/40">
          <TableRow>
            <TableHead className={cn(HEAD, "w-[25%]")}>
              {t("admin.owners.col.owner", "Owner")}
            </TableHead>
            <TableHead className={cn(HEAD, "w-[16%]")}>
              <span className="inline-flex items-center gap-1">
                {t("admin.owners.col.access", "Access")}
                <AccessHelpTrigger t={t} />
              </span>
            </TableHead>
            {/* 12% — the header word "Apartments" is a single unbreakable
                token ~98px wide at this size, so anything narrower clipped it
                against the next column instead of wrapping. */}
            <TableHead className={cn(HEAD, "text-center", "w-[12%]")}>
              {t("admin.owners.col.apartments", "Apartments")}
            </TableHead>
            <TableHead className={cn(HEAD, "w-[21%]")}>
              {t("admin.owners.col.property", "Property")}
            </TableHead>
            <TableHead className={cn(HEAD, "w-[16%]")}>
              {t("admin.owners.col.status", "Status")}
            </TableHead>
            <TableHead className={cn(HEAD, "text-right", "w-[10%]")}>
              {t("admin.owners.col.actions", "Actions")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const o = row.lessor;
            const key = row.key;
            const isOpen = expanded.has(key);
            // Properties come from the billing side when there is one, and from
            // the account's assignments when there isn't.
            const properties =
              o?.propertyList ??
              accountPropertyList(row.account, propertiesById);
            return (
              <Fragment key={key}>
                <TableRow
                  onClick={() => onRowClick(row)}
                  className={cn(
                    "group cursor-pointer border-b border-border/60 transition-colors hover:bg-muted/40",
                    isOpen && "bg-muted/30"
                  )}
                >
                  {/* Owner — avatar + identity, with a hover accent rail. */}
                  <TableCell className="relative p-4">
                    <span className="pointer-events-none absolute inset-y-0 left-0 w-[3px] bg-primary opacity-0 transition-opacity group-hover:opacity-100" />
                    <OwnerIdentity row={row} t={t} />
                  </TableCell>

                  {/* Access — which halves exist, and what the login is doing */}
                  <TableCell className="p-4">
                    <AccessCell row={row} onFixLink={onFixLink} />
                  </TableCell>

                  {/* Apartments — count chip; expands the per-apartment list. */}
                  <TableCell className="p-4 text-center">
                    <ApartmentsChip
                      owner={o}
                      isOpen={isOpen}
                      onToggle={() => toggle(key)}
                      t={t}
                    />
                  </TableCell>

                  {/* Property — the buildings this owner's apartments sit in,
                      property-level, matching the purple tags in owner
                      settings. Always its own column: it used to fold under
                      the owner's name below xl, which read as the owner cell
                      sprouting a second identity. */}
                  <TableCell className="p-4">
                    <PropertyCell
                      owner={o}
                      properties={properties}
                      open={propsOpen.has(key)}
                      onToggle={() => togglePropsTag(key)}
                      t={t}
                    />
                  </TableCell>

                  {/* Status — aggregate onboarding stage (lowest across properties) */}
                  <TableCell className="p-4">
                    <StatusCell owner={o} t={t} />
                  </TableCell>

                  {/* Actions — full owner + account menu (shared component) */}
                  <TableCell className="p-4 text-right">
                    <RowActions
                      row={row}
                      owner={o}
                      isSuperAdmin={isSuperAdmin}
                      onRowClick={onRowClick}
                      onEditOwner={onEditOwner}
                      onEditAccount={onEditAccount}
                      onDelete={onDelete}
                      onSendLoginLink={onSendLoginLink}
                      onFixLink={onFixLink}
                    />
                  </TableCell>
                </TableRow>

                {/* Expanded per-apartment breakdown: each apartment, the
                    property it sits in, and that property's onboarding stage. */}
                {isOpen && o && (
                  <TableRow className="border-b border-border/60 bg-muted/20 hover:bg-muted/20">
                    <TableCell colSpan={6} className="py-0">
                      <ApartmentBreakdown
                        owner={o}
                        t={t}
                        className="pl-[4.75rem] pr-4"
                      />
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
